import React, { createContext, useCallback, useContext, useEffect, useRef, useState } from 'react';
import { API_CONFIG } from '../config/api';

const AppStateContext = createContext(null);

export const useAppState = () => {
  const ctx = useContext(AppStateContext);
  if (!ctx) throw new Error('useAppState must be used within AppStateProvider');
  return ctx;
};

export const AppStateProvider = ({ children }) => {
  const [authToken, setAuthToken] = useState('');
  const [currentUser, setCurrentUser] = useState(null);
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [sessionChecked, setSessionChecked] = useState(false);

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const [users, setUsers] = useState([]);
  const [products, setProducts] = useState([]);
  const [scanHistory, setScanHistory] = useState([]);
  const [failedScans, setFailedScans] = useState([]);

  const [dataLoaded, setDataLoaded] = useState({ users: false, products: false, scanHistory: false, failedScans: false });
  const loadingRefs = useRef({ users: false, products: false, scanHistory: false, failedScans: false });

  const apiCall = useCallback(async (endpoint, method = 'GET', body = null, requiresAuth = true) => {
    try {
      const url = `${API_CONFIG.baseURL}${endpoint}`;
      const headers = {
        'Content-Type': 'application/json',
        ...(requiresAuth && authToken && { Authorization: `Bearer ${authToken}` }),
      };
      const config = { method, headers, ...(body && { body: JSON.stringify(body) }) };
      const response = await fetch(url, config);
      const data = await response.json();
      if (!response.ok) throw new Error(data.message || 'API call failed');
      return data;
    } catch (err) {
      setError(err.message);
      throw err;
    }
  }, [authToken]);

  const handleRegister = async (registerData) => {
    setLoading(true);
    setError('');
    try {
      const response = await fetch(`${API_CONFIG.baseURL}${API_CONFIG.endpoints.register}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(registerData),
      });
      const data = await response.json();
      if (!response.ok) throw new Error(data.message || 'Registration failed');

      if (data.status && data.token) {
        setAuthToken(data.token);
        localStorage.setItem('authToken', data.token);
      }
      return { success: true, data };
    } catch (err) {
      setError(err.message);
      throw err;
    } finally {
      setLoading(false);
    }
  };

  // Creates the company then completes the register->company->login flow:
  // the caller already holds an authToken from registration but isn't
  // "logged in" yet until they have a company.
  const createCompany = async (companyData) => {
    const response = await fetch(`${API_CONFIG.baseURL}${API_CONFIG.endpoints.createCompany}`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${authToken}` },
      body: JSON.stringify(companyData),
    });
    const data = await response.json();
    if (!response.ok) throw new Error(data.message || 'Company creation failed');

    const user = data.user || data.data?.user;
    if (user) {
      setCurrentUser(user);
      setIsLoggedIn(true);
      localStorage.setItem('currentUser', JSON.stringify(user));
      setDataLoaded({ users: false, products: false, scanHistory: false, failedScans: false });
      await loadDashboardData(authToken);
    }
    return data;
  };

  const loadUsers = useCallback(async (token = authToken, force = false) => {
    if (loadingRefs.current.users || (!force && dataLoaded.users && users.length > 0)) return;
    loadingRefs.current.users = true;
    try {
      const response = await fetch(`${API_CONFIG.baseURL}${API_CONFIG.endpoints.users}?&page=1&limit=10000`, {
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
      });
      const data = await response.json();
      if (response.ok && data.success) {
        setUsers(data.users || []);
        setDataLoaded((prev) => ({ ...prev, users: true }));
      }
    } catch (err) {
      console.error('Failed to load users:', err);
    } finally {
      loadingRefs.current.users = false;
    }
  }, [authToken, dataLoaded.users, users.length]);

  const loadProducts = useCallback(async (token = authToken, force = false) => {
    if (loadingRefs.current.products || (!force && dataLoaded.products && products.length > 0)) return;
    loadingRefs.current.products = true;
    try {
      const response = await fetch(`${API_CONFIG.baseURL}${API_CONFIG.endpoints.products}?page=1&limit=10000`, {
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
      });
      const data = await response.json();
      if (response.ok && data.success) {
        setProducts(data.products || []);
        setDataLoaded((prev) => ({ ...prev, products: true }));
      }
    } catch (err) {
      console.error('Failed to load products:', err);
    } finally {
      loadingRefs.current.products = false;
    }
  }, [authToken, dataLoaded.products, products.length]);

  const loadScanHistory = useCallback(async (token = authToken, force = false) => {
    if (loadingRefs.current.scanHistory || (!force && dataLoaded.scanHistory && scanHistory.length > 0)) return;
    loadingRefs.current.scanHistory = true;
    try {
      const response = await fetch(`${API_CONFIG.baseURL}${API_CONFIG.endpoints.scanHistory}?page=1&limit=10000`, {
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
      });
      const data = await response.json();
      if (response.ok && data.success) {
        setScanHistory(data.products || []);
        setDataLoaded((prev) => ({ ...prev, scanHistory: true }));
      }
    } catch (err) {
      console.error('Failed to load scan history:', err);
    } finally {
      loadingRefs.current.scanHistory = false;
    }
  }, [authToken, dataLoaded.scanHistory, scanHistory.length]);

  // Failed/invalid scan attempts — signature mismatches, unknown barcodes,
  // etc. Distinct from scanHistory (which only covers products that reached
  // at least one *verified* step): this surfaces scans that never got that
  // far, straight from the raw ScanLog collection.
  const loadFailedScans = useCallback(async (token = authToken, force = false) => {
    if (loadingRefs.current.failedScans || (!force && dataLoaded.failedScans && failedScans.length > 0)) return;
    loadingRefs.current.failedScans = true;
    try {
      const response = await fetch(`${API_CONFIG.baseURL}${API_CONFIG.endpoints.scanLogs}?page=1&limit=10000&sortBy=createdAt&sortOrder=desc`, {
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
      });
      const data = await response.json();
      if (response.ok && data.success) {
        setFailedScans((data.logs || []).filter((log) => log.result !== 'SUCCESS'));
        setDataLoaded((prev) => ({ ...prev, failedScans: true }));
      }
    } catch (err) {
      console.error('Failed to load failed scans:', err);
    } finally {
      loadingRefs.current.failedScans = false;
    }
  }, [authToken, dataLoaded.failedScans, failedScans.length]);

  const loadDashboardData = useCallback(async (token = authToken, force = false) => {
    if (!token) return;
    setLoading(true);
    try {
      await Promise.all([loadUsers(token, force), loadProducts(token, force), loadScanHistory(token, force), loadFailedScans(token, force)]);
    } catch (err) {
      console.error('Failed to load dashboard data:', err);
    } finally {
      setLoading(false);
    }
  }, [loadUsers, loadProducts, loadScanHistory, loadFailedScans, authToken]);

  // `force` bypasses the dataLoaded/loadingRefs checks directly, so this
  // doesn't depend on setDataLoaded's reset landing before loadDashboardData
  // reads it (state updates aren't synchronous, so that ordering can't be
  // relied on).
  const handleRefresh = useCallback(async () => {
    await loadDashboardData(authToken, true);
  }, [loadDashboardData, authToken]);

  const login = async (email, password) => {
    setLoading(true);
    setError('');
    try {
      const url = `${API_CONFIG.baseURL}${API_CONFIG.endpoints.login}`;
      const response = await fetch(url, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email, password }),
      });
      const data = await response.json();
      if (!response.ok || !data.success) {
        setError(data.message || 'Login failed. Please check your credentials.');
        return { success: false };
      }
      setAuthToken(data.token);
      setCurrentUser(data.user);
      setIsLoggedIn(true);
      localStorage.setItem('authToken', data.token);
      localStorage.setItem('currentUser', JSON.stringify(data.user));
      setDataLoaded({ users: false, products: false, scanHistory: false, failedScans: false });
      await loadDashboardData(data.token);
      return { success: true, user: data.user };
    } catch (err) {
      setError('Login failed. Please check your credentials.');
      return { success: false };
    } finally {
      setLoading(false);
    }
  };

  const logout = () => {
    setAuthToken('');
    setCurrentUser(null);
    setIsLoggedIn(false);
    setUsers([]);
    setProducts([]);
    setScanHistory([]);
    setFailedScans([]);
    setDataLoaded({ users: false, products: false, scanHistory: false, failedScans: false });
    loadingRefs.current = { users: false, products: false, scanHistory: false, failedScans: false };
    localStorage.removeItem('authToken');
    localStorage.removeItem('currentUser');
  };

  // Session restore — runs once on mount.
  useEffect(() => {
    const storedToken = localStorage.getItem('authToken');
    const storedUser = localStorage.getItem('currentUser');
    if (storedToken && storedUser && storedUser !== 'undefined') {
      try {
        const parsedUser = JSON.parse(storedUser);
        setAuthToken(storedToken);
        setCurrentUser(parsedUser);
        setIsLoggedIn(true);
        loadDashboardData(storedToken);
      } catch (err) {
        console.error('Error parsing stored user:', err);
        localStorage.removeItem('authToken');
        localStorage.removeItem('currentUser');
      }
    }
    setSessionChecked(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const value = {
    authToken,
    currentUser,
    isLoggedIn,
    sessionChecked,
    loading,
    error,
    setError,
    users,
    products,
    scanHistory,
    failedScans,
    dataLoaded,
    setDataLoaded,
    setUsers,
    setProducts,
    apiCall,
    handleRegister,
    createCompany,
    login,
    logout,
    loadUsers,
    loadProducts,
    loadScanHistory,
    loadFailedScans,
    loadDashboardData,
    handleRefresh,
  };

  return <AppStateContext.Provider value={value}>{children}</AppStateContext.Provider>;
};
